import { CustomFieldType } from '__types__';
import type { EnumFilter } from 'v5/api/_common/query/filters/EnumFilter';
import type { FieldNameTypeMapping } from 'v5/core/custom-fields/custom-fields-filters.http-converters';
import type {
  FilterConfigBase,
  FilterEnumConfig,
  FilterStringConfig
} from 'v5/patterns/filters/FilterWidget.types';

const ARRAY_OPERATORS: EnumFilter<unknown>['operator'][] = [
  'matchAny',
  'matchAll',
  'matchNone'
];

/** Core text fields a template stores as `{eq}`. */
const TEXT_CORE_FIELDS = new Set([
  'role',
  'emails',
  'phone_numbers',
  'websites',
  'socials',
  'locations'
]);

function textOf(config: FilterConfigBase): FilterStringConfig | null {
  if (config.type !== 'string') return null;
  return {
    ...config,
    createDefaultValue: () => ({ operator: 'equals', value: '' }),
    options: { ...config.options, operators: ['equals'] }
  };
}

function enumOf(
  config: FilterConfigBase,
  selection: 'single' | 'array'
): FilterEnumConfig<unknown, unknown> | null {
  if (config.type !== 'enum') return null;
  switch (selection) {
    case 'single':
      return {
        ...config,
        options: {
          ...config.options,
          operators: ['matchAny'],
          selectionMode: 'single'
        }
      };
    case 'array':
      return {
        ...config,
        options: { ...config.options, operators: ARRAY_OPERATORS }
      };
  }
}

function customFieldOf(
  type: CustomFieldType | undefined,
  config: FilterConfigBase
): FilterConfigBase | null {
  switch (type) {
    case CustomFieldType.SHORT_TEXT:
    case CustomFieldType.LONG_TEXT:
    case CustomFieldType.URL:
      return textOf(config);
    case CustomFieldType.NUMBER:
    case CustomFieldType.PERCENTAGE:
    case CustomFieldType.CURRENCY:
    case CustomFieldType.MEASUREMENT:
    case CustomFieldType.MEASUREMENT_NEW:
    case CustomFieldType.HEIGHT:
      return config.type === 'number' ? config : null;
    case CustomFieldType.BOOLEAN:
      return config.type === 'boolean' ? config : null;
    case CustomFieldType.SINGLE_SELECT:
      return enumOf(config, 'single');
    case CustomFieldType.MULTI_SELECT:
      return enumOf(config, 'array');
    default:
      return null;
  }
}

function reportConfigOf(
  key: string,
  config: FilterConfigBase,
  fieldTypes: FieldNameTypeMapping
): FilterConfigBase | null {
  if (key === 'tags' || key === 'groups') return enumOf(config, 'array');
  if (TEXT_CORE_FIELDS.has(key)) return textOf(config);
  if (/^custom_\d+$/.test(key)) return customFieldOf(fieldTypes[key], config);
  return null;
}

/**
 * The resource filters a report template can store (`resourceSearchToV1`), each offering only the
 * operators with a stored shape: text is "is", a single select takes one value, dates, owners,
 * workspaces and the type (a select of its own) are left out.
 */
export function reportResourceFilterConfigsOf(
  configs: Record<string, FilterConfigBase>,
  fieldTypes: FieldNameTypeMapping
): Record<string, FilterConfigBase> {
  return Object.fromEntries(
    Object.entries(configs).flatMap(([key, config]) => {
      const reportConfig = reportConfigOf(key, config, fieldTypes);
      return reportConfig ? [[key, reportConfig]] : [];
    })
  );
}
